import { BusinessCalendar } from './calendars';
import { Category, Image } from './common';
import { BusinessService } from './services';
import { UserRoles, UserRoleScopes } from './userRoles';

export enum PaymentOptions {
  CashOnly = 'cash',
  CardOnly = 'card',
  CashAndCard = 'cashCard',
  InApp = 'inApp',
  Bank = 'bank',
}

export enum CompanyContactType {
  Phone = 'phone',
  Email = 'email',
  Website = 'website',
}

export enum CompanyStatus {
  Enabled = 'enabled',
  Disabled = 'disabled',
  DisabledByOwner = 'disabledByOwner',
  InReview = 'inReview',
}

export interface Company {
  id: string;
  role: string;
  title: string;
  thumbnail: string;
  stripeEnabled: boolean;
  stripeInfoRequired: boolean;
  currency: string;
}

export interface CompanyTextTemplate {
  companyId: string;
  template: string;
  type: string;
}

export interface CompanyTranslation {
  description: string;
  keywords: string;
  language: string;
  tagline: string;
  title: string;
}

export interface CompanyContact {
  type: string;
  value?: string;
}

export interface CompanyContent {
  category: Category;
  country: string;
  genderPreference: string;
  paymentOption: string;
  workHours: string[];
  address: string;
  latitude: number;
  longitude: number;
}

export interface CompanySettings {
  currency: string;
}

export enum CompanySubscriptionStatus {
  Active = 'active',
  Missing = 'missing',
  GracePeriod = 'grace',
  Postponed = 'postponed',
}

export interface BooklaAdminCompanySettings {
  montonioPaymentsEnabled: boolean;
  smsCampaignsEnabled: boolean;
  applyFirstPurchaseFee: boolean;
}

export interface CompanyDetails {
  id: string;
  status: CompanyStatus;
  settings: CompanySettings;
  image: Image;
  contacts: CompanyContact[];
  content: CompanyContent;
  translations: CompanyTranslation[];
  hasSignedAgreement: boolean;
  subscriptionStatus: CompanySubscriptionStatus;
  timeZone: string;
  companyCredit?: number;
  adminSettings?: BooklaAdminCompanySettings;
}

export interface UpdateCompanyInfoRequest {
  genderPreference: string;
  paymentOptionsType: string;
  workHours: number[];
}

export interface CompanyInvoice {
  companyId: string;
  invoiceNumber: string;
  date: string;
  type: CompanyInvoiceType;
}

export enum CompanyInvoiceType {
  Report = 'report',
}

export enum CompanySubscriptionType {
  Checkin = 'checkin',
  Pro = 'pro',
}

export const CompanySubscriptionPrice = {
  Checkin: process.env.REACT_APP_CHECKIN_SUB_PRICE_ID,
} as const;

export type CompanySubscriptionPriceType =
  typeof CompanySubscriptionPrice[keyof typeof CompanySubscriptionPrice];

export interface SubscriptionCheckoutURL {
  url: string;
  stripeId: string;
}

export interface CompanyLegalInfo {
  id?: string;
  companyName: string;
  regNumber: string;
  vatNumber?: string;
  vatPercent?: number;
  address: string;
  phoneNumber: string;
  email: string;
  firstName: string;
  lastName: string;
  position: string;
  bankName?: string;
  bankAccountNumber?: string;
}

type BusinessCompanyDetailsCalendar = Pick<
  BusinessCalendar,
  'id' | 'title' | 'type'
> & { thumbnail?: string; disabled: boolean };

type BusinessCompanyDetailsService = Pick<
  BusinessService,
  'id' | 'title' | 'type' | 'scheduleColor'
> & { thumbnail?: string; disabled: boolean };

export interface BusinessCompanyDetails {
  id: string;
  timeZone: string;
  calendars: BusinessCompanyDetailsCalendar[];
  services: BusinessCompanyDetailsService[];
  status: CompanyStatus;
  currency: string;
}

export interface CompanyStateChange {
  wasDisabled: boolean;
}

export enum BooklaReference {
  friend = 'friend',
  searchEngine = 'search engine',
  socialMedia = 'social media',
  other = 'other',
}

export interface CompanySetupParams {
  address: string;
  categoryId: string;
  latitude: number;
  longitude: number;
  reference: BooklaReference;
  type?: string;
  workHours: string[];
  hasWebsite: boolean;
  turnover: number;
  isPrioritizedCategory: boolean;
  isInterestedInKiosks?: boolean;
  teamSize: string;
  employeesRange?: number;
}

export enum FeatureFlagProductState {
  enabled = 'enabled',
  disabled = 'disabled',
  paused = 'paused',
}

export interface FeatureFlags {
  covidVerifyAvailable: boolean;
  products: FeatureFlagProductState;
  sms_campaigns: boolean;
}

export interface AccessConfigResponse {
  features: FeatureFlags;
  role: UserRoles;
  scopes?: UserRoleScopes[];
}
